import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import classnames from 'classnames';
import { head } from 'lodash';
import uuid from 'node-uuid';
import { Column, FlexColumn, FlexRow, Row, ServicingSection } from 'web-ui-components/lib/global/layout';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';
import { Label } from 'web-ui-components/lib/atoms/forms';
import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import withAccountSelection from '../../../containers/withAccountSelection';
import SiteHeader from '../../../sharedComponents/SiteHeader/SiteHeader';
import { FromAccount } from '../../../sharedComponents/FromAccount/FromAccount';
import { ToAccount } from '../../../sharedComponents/ToAccount/ToAccount';
import { getAccountsForBacsOnType, getInternalAccountsForBacs } from '../../../states/accounts/selectors';
import { getPayees } from '../../../states/payees/selectors';
import { fromAccountPropType, toAccountPropType } from '../../../states/accounts/propTypes';
import payeePropTypes from '../../../states/payees/propTypes';
import pageContent, { contentPropTypes } from './Debit.content';
import {
	addMultiplePayments,
	updateToAccount,
	updateSubmissionDate,
	updatePaymentName,
	updateBacsDebitImportedPayments,
} from '../../../states/bacsDebit/actions';
import {
	getToAccount,
	getBacsId,
	getSubmissionDate,
	getBacsPaymentName,
	getSettlementDate,
} from '../../../states/bacsDebit/selectors';
import { goToDebitPaymentsPage, resetApp, pages, goToPage } from '../../../states/page/actions';
import { BACS_DEBIT_CODE, TYPE_BACS_DEBIT } from '../../../config';
import PaymentTabs from '../../../paymentAppSelector/PaymentTabs';
import TemplateSelector from '../../../sharedComponents/TemplateSelector/TemplateSelector';
import { fetchTemplates, createBacsDebitTemplate } from '../../../states/templates/actions';
import Notifications from '../../../sharedComponents/Notifications';
import { setSelectedFromAccount } from '../../../states/payeeSelector/actions';
import { contentWrapper as FileUploadRevealBlock } from '../FileUploadRevealBlock';
import FileUploadFeatureFlag from '../../../sharedComponents/FileUploadFeatureFlag/FileUploadFeatureFlag';
import { CONST_FILE_UPLOAD } from '../../../config/constants';
import { initBacsValidation } from '../../../states/validation/actions';

const mapState = (state) => ({
	destinationAccount: getToAccount(state),
	destinationAccounts: getAccountsForBacsOnType(state, BACS_DEBIT_CODE),
	toPayees: getPayees(state),
	bacsId: getBacsId(state),
	submissionDate: getSubmissionDate(state),
	paymentName: getBacsPaymentName(state),
	sourceAccounts: getInternalAccountsForBacs(state, getToAccount(state)),
	settlementDate: getSettlementDate(state),
});

const mapDispatch = (dispatch) => ({
	handleSelectDestinationAccount: (accountId) => dispatch(updateToAccount({ accountId })),
	addMultiplePayments: (payments) => dispatch(addMultiplePayments(payments)),
	continueToPayments: () => dispatch(goToDebitPaymentsPage()),
	handleCreateNewTemplate: () => dispatch(createBacsDebitTemplate()),
	fetchTemplates: () => dispatch(fetchTemplates()),
	reset: () => dispatch(resetApp),
	goToPayeeSelector: () => dispatch(goToPage(pages.BacsDebitPayeeSelector)),
	setSelectedDestinationAccount: (account) => dispatch(setSelectedFromAccount(account)),
	continueToImportedDebitPayments: () => dispatch(goToPage(pages.BacsImportedDebitPaymentPage)),
	handlePaymentNameChange: (paymentName) => dispatch(updatePaymentName({ paymentName })),
	handleDateChange: (submissionDate) => {
		dispatch(updateSubmissionDate({ submissionDate }));
	},
	initBacsValidation: () => {
		dispatch(initBacsValidation());
	},
	updateBacsDebitImportedPayments: (importedPaymentsList) =>
		dispatch(updateBacsDebitImportedPayments(importedPaymentsList)),
});

class DebitLandingPage extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showUploadButton: false,
			importedPaymentsList: [],
			paymentName: '',
		};

		this.handleToCardClick = this.handleToCardClick.bind(this);
		this.handleAddPayee = this.handleAddPayee.bind(this);
		this.handleFileUploadButtonClick = this.handleFileUploadButtonClick.bind(this);
		this.handleChooseFileButtonClick = this.handleChooseFileButtonClick.bind(this);
	}

	componentDidMount() {
		const {
			destinationAccounts,
			handleSelectDestinationAccount,
			fetchTemplates,
			initBacsValidation,
			reset,
			setSelectedDestinationAccount,
		} = this.props;
		reset();
		initBacsValidation();
		if (destinationAccounts.length === 1) {
			handleSelectDestinationAccount(head(destinationAccounts).id);
			setSelectedDestinationAccount(head(destinationAccounts).id);
		}
		fetchTemplates();
	}

	handleAddPayee(event) {
		const generatedPaymentId = uuid.v1();
		const payment = {
			id: generatedPaymentId,
			fromAccount: '',
			reference: '',
		};
		const { destinationAccount, submissionDate, paymentName, settlementDate, bacsId } = this.props;
		const paymentsWithBacsDetails = {
			payments: [payment],
			paymentDetails: {
				bacsDetails: {
					bacsId,
					toAccount: destinationAccount,
					paymentName,
					submissionDate,
					settlementDate,
				},
			},
			selectedPaymentId: generatedPaymentId,
			accountToAddPayee: destinationAccount,
		};
		this.props.handleAddPayee({ paymentType: 'BACS_DEBIT', event, paymentsWithDetails: paymentsWithBacsDetails });
	}

	handleToCardClick() {
		this.props.goToPayeeSelector();
	}

	handleFileUploadButtonClick() {
		const { continueToImportedDebitPayments, handlePaymentNameChange, updateBacsDebitImportedPayments } = this.props;
		handlePaymentNameChange(this.state.paymentName);
		updateBacsDebitImportedPayments(this.state.importedPaymentsList);
		continueToImportedDebitPayments();
	}

	handleChooseFileButtonClick(importedPaymentsList, paymentName, hasError) {
		this.setState({
			showUploadButton: !hasError,
			importedPaymentsList,
			paymentName,
		});
	}

	render() {
		const {
			destinationAccounts,
			toPayees,
			content,
			destinationAccount,
			handleSelectPaymentType,
			sourceAccounts,
			handleCreateNewTemplate,
			isBootStrapInProgress,
			showFromAccountSelector,
			handleFromCardClick,
			submissionDate,
			settlementDate,
			handleDateChange,
			handleSelectFromAccount,
		} = this.props;
		const { wrapperTitle, shortDescription, fromLabel, toLabel } = content;
		const { showUploadButton } = this.state;

		const wrapperClass = classnames({
			'c-o-payee-list--from-state': showFromAccountSelector,
		});

		return (
			<div className={wrapperClass}>
				<SiteHeader title={wrapperTitle} />
				<LoadingLocalTakeover show={isBootStrapInProgress} title="loading...">
					<div>
						<ServicingSection>
							<div className="u-margin-bottom-2">
								<PaymentTabs handleSelectTab={handleSelectPaymentType} activeTab={TYPE_BACS_DEBIT} />
							</div>
							<div className="u-margin-bottom-2">
								<Notifications />
							</div>
							<p>{shortDescription}</p>
							<Row>
								<Column md={12}>
									<FlexRow>
										<FlexColumn gutter={1}>
											<Label className="u-type-size-uist" htmlFor="fromAccount">
												{toLabel}
											</Label>
											<FromAccount
												id="fromAccount"
												fromAccount={destinationAccount}
												showAccountSelector={showFromAccountSelector}
												fromAccounts={destinationAccounts}
												handleSelectFromAccount={handleSelectFromAccount}
												handleCardClick={handleFromCardClick}
												title={toLabel}
											/>
										</FlexColumn>
										<FlexColumn gutter={1}>
											<Label className="u-type-size-uist" htmlFor="toAccount">
												{fromLabel}
											</Label>
											<ToAccount
												id="toAccount"
												handleCardClick={this.handleToCardClick}
												showAccountSelector={false}
												toPayees={toPayees}
												toAccounts={sourceAccounts}
												fromAccount={destinationAccount}
												disable={!destinationAccount}
												title={fromLabel}
											/>
										</FlexColumn>
									</FlexRow>
								</Column>
							</Row>
							<TemplateSelector paymentType={TYPE_BACS_DEBIT} handleCreateNewTemplate={handleCreateNewTemplate} />
							<FileUploadFeatureFlag path={CONST_FILE_UPLOAD.BACS_DEBIT}>
								<FileUploadRevealBlock
									type={TYPE_BACS_DEBIT}
									submissionDate={submissionDate}
									settlementDate={settlementDate}
									showUploadButton={showUploadButton}
									handleDateChange={handleDateChange}
									handleFileUploadButtonClick={this.handleFileUploadButtonClick}
									handleChooseFileButtonClick={this.handleChooseFileButtonClick}
								/>
							</FileUploadFeatureFlag>
						</ServicingSection>
					</div>
				</LoadingLocalTakeover>
			</div>
		);
	}
}

DebitLandingPage.propTypes = {
	content: PropTypes.shape(contentPropTypes).isRequired,
	handleSelectPaymentType: PropTypes.func.isRequired,
	handleSelectDestinationAccount: PropTypes.func.isRequired,
	destinationAccounts: PropTypes.arrayOf(PropTypes.shape(fromAccountPropType)).isRequired,
	destinationAccount: PropTypes.string,
	toPayees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
	sourceAccounts: PropTypes.arrayOf(PropTypes.shape(toAccountPropType)).isRequired,
	handleAddPayee: PropTypes.func.isRequired,
	bacsId: PropTypes.string,
	submissionDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(moment)]),
	paymentName: PropTypes.string,
	settlementDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(moment)]),
	handleCreateNewTemplate: PropTypes.func.isRequired,
	fetchTemplates: PropTypes.func,
	initBacsValidation: PropTypes.func.isRequired,
	isBootStrapInProgress: PropTypes.bool.isRequired,
	goToPayeeSelector: PropTypes.func.isRequired,
	handleSelectFromAccount: PropTypes.func.isRequired,
	handleFromCardClick: PropTypes.func.isRequired,
	showFromAccountSelector: PropTypes.bool.isRequired,
	handleDateChange: PropTypes.func,
	continueToImportedDebitPayments: PropTypes.func.isRequired,
	handlePaymentNameChange: PropTypes.func.isRequired,
	updateBacsDebitImportedPayments: PropTypes.func.isRequired,
	reset: PropTypes.func.isRequired,
	setSelectedDestinationAccount: PropTypes.func.isRequired,
};

export const connectWrapper = connect(mapState, mapDispatch)(withAccountSelection(DebitLandingPage));
const contentWrapper = withContent({ content: pageContent });
export default withScrollTop(contentWrapper(connectWrapper));
